import { showError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { Logger } from '@calendly/logging-service'
import { createApi } from '@reduxjs/toolkit/query/react'
import { snakeCase, camelCase, identity } from 'lodash-es'

import { redirectTo } from 'actions/core/navigation'
import { extractErrorMessage } from 'shared/utils'

const DESTROY_KEY = '_destroy'

const createTransformer = (processor = identity) => {
  const transform = (value) => {
    if (Array.isArray(value)) {
      return value.map(transform)
    }

    if (typeof value === 'object' && value !== null) {
      const result = Object.fromEntries(
        Object.entries(value).map(
          ([key, nestedValue]) => [key === DESTROY_KEY ? key : processor(key), transform(nestedValue)]
        ))

      Object.entries(Object.getOwnPropertyDescriptors(value)).forEach(([propertyKey, propertyValue]) => {
        if (propertyValue.enumerable) return

        Object.defineProperty(result, propertyKey === DESTROY_KEY ? propertyKey : processor(propertyKey), propertyValue)
      })

      return result
    }

    return value
  }

  return transform
}

const transformRequestFunc = createTransformer(snakeCase)
const transformResponseFunc = createTransformer(camelCase)

const httpRequestBaseQuery = async (
  arg,
  { dispatch, getState },
  {
    onSuccess,
    handleErrors = true,
    transformRequest = true,
    transformResponse = true
  } = {}
) => {
  if (typeof arg === 'function') {
    arg = arg({ getState })
  }
  const { url, method, data, params, validateStatus, ...other } = arg

  const [success, rawResponse] = await httpRequest({
    url,
    method,
    data: transformRequest ? transformRequestFunc(data) : data,
    params: transformRequest ? transformRequestFunc(params) : params,
    ...(typeof validateStatus === 'function' ? { validateStatus } : {})
  })

  if (success) {
    const response = transformResponse ? transformResponseFunc(rawResponse) : rawResponse
    if (onSuccess) {
      onSuccess(response, { dispatch, getState }, { data, params, ...other })
    }
    return { data: response }
  }

  if (handleErrors) {
    if (rawResponse.type === 'rate_limit_error') {
      dispatch(showError(I18n.t('errors.system.rate_limited')))
      return {
        error: {
          ...transformResponseFunc(rawResponse),
          message: I18n.t('errors.system.rate_limited'),
        },
      }
    }

    if (rawResponse.type === 'auth' && window.location.pathname !== '/extensions/client_logout') {
      // check user session endpoint to see if the user is truly authenticated
      const [hasValidSession, sessionResponse] = await httpRequest({
        url: '/api/account/echo',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const loggingData = { hasValidSession, initialApiUrl: url, initialApiResponse: rawResponse, sessionResponse }
      // if they have a valid session, log the issue
      if (hasValidSession) {
        Logger.error(
          'User experienced an unauthorized response from an API call even through they have a valid session.',
          loggingData
        )
      } else {
        Logger.error(
          'User experienced an unauthorized response from an API call while also having an invalid session.',
          loggingData
        )
      }
      // whether they have a valid session or not because we have an issue, log them out. Redirect to app/login
      await httpRequest({
        method: 'DELETE',
        url: '/users/sessions',
        data: null,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
      dispatch(showError(I18n.t('errors.api.authentication_error')))
      dispatch(redirectTo('/app/login'))
    }
    if (!['request_aborted', 'validation'].includes(rawResponse.type)) {
      // Extract error message, checking both transformed and untransformed formats
      const transformedError = transformResponse ? transformResponseFunc(rawResponse) : rawResponse
      const errorMessage = extractErrorMessage(transformedError)
        || extractErrorMessage(rawResponse)
        || rawResponse.message
      dispatch(showError(errorMessage))
    }
  }

  return { error: transformResponseFunc(rawResponse) }
}

export const rootApi = createApi({
  baseQuery: httpRequestBaseQuery,
  endpoints: () => ({})
})

const serviceTokenApi = rootApi.injectEndpoints({
  endpoints: (build) => ({
    getUserServiceToken: build.query({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/api/restricted/user_service_tokens',
          method: 'POST',
        })

        if (result.error) {
          return result
        }

        return { data: result.data?.resource?.serviceToken ?? null }
      },
    }),
  }),
})

export const { getUserServiceToken } = serviceTokenApi.endpoints
